import type { Dictionary } from "./dictionary";

// English dictionary — kept in exact structural sync with pl.ts via the
// shared Dictionary type; a missing/extra key here is a type error.
export const en: Dictionary = {
  brand: "GoForCoach",

  validation: {
    required: "Please fill in this field.",
    typeMismatch: "Please enter a valid value.",
    tooShort: (min) => `Please enter at least ${min} characters.`,
    tooLong: (max) => `Please enter at most ${max} characters.`,
    rangeUnderflow: (min) => `Value must be at least ${min}.`,
    rangeOverflow: (max) => `Value must be at most ${max}.`,
    patternMismatch: "Please match the requested format.",
    badInput: "Please enter a valid value.",
  },

  nav: {
    home: "Home",
    findTrainer: "Find a trainer",
    becomeTrainer: "Become a trainer",
    forTrainers: "For trainers",
    search: "Search",
    trainerCard: "Trainer card",
    howItWorks: "How it works",
    trainerPanel: "Trainer panel",
    clientPanel: "My trainings",
    openMenu: "Open menu",
    closeMenu: "Close menu",
    mainMenu: "Main menu",
    sections: "Sections",
    sectionsTitle: "Jump to section",
    language: "Language",
  },

  trainerPanel: {
    trainerTitle: "Trainer panel",
    clientTitle: "Client panel",
    trainerSub: "Your full training schedule.",
    clientSub: "Your upcoming bookings.",
    noUpcomingTrainer: "No upcoming sessions on the schedule.",
    sessionListEarlier: "Earlier sessions",
    sessionListLater: "Later sessions",
    noUpcomingClient: "You have no training scheduled yet.",
    withTrainer: "with",
    eventCapacity: (n: number) => `${n} people`,
    groupCardName: (name: string) => `Group: ${name}`,
    showMoreTrainings: (hidden: number) => `Show more (${hidden})`,
    showFewerTrainings: "Show fewer",
    selectTraining: "Select a training from the list to see details.",
    backToCalendar: "Back to calendar",
    calendarOptions: "Calendar options",
    addTraining: "Add training",
    deselectDay: "Deselect day",
    openInMaps: "Open in maps",
    copyAddress: "Copy address",
    addressCopied: "Address copied",
    heroClients: "Clients",
    heroTrainers: "Trainers",
    heroHours: "Hours total",
    heroNext: "Next session",
    needsAttentionTitle: "Needs attention",
    needsAttentionEmpty: "No new requests — new bookings and client requests will show up here as soon as they arrive.",
    scheduleTitle: "Schedule",
    tabsLabel: "Panel navigation",
    navStatus: "Profile status",
    navCard: "Trainer card",
    navOffer: "Offer, hours & places",
    navCalendar: "Calendar",
    navStats: "Stats",
    cardView: "View public profile",
    setupDone: "Account ready",
    setupTitle: "Account setup",
    contactTitle: "Contact details",
    setupHeading: (remaining: number) => `Account setup · ${remaining} ${remaining === 1 ? "step" : "steps"} left`,
    setupMissingLead: "Missing:",
    setupAwaitingEmail: "Confirm your email to create your trainer account.",
    clientsTitle: "Clients you train with",
    clientsEmpty: "You haven't trained with any client yet.",
    statsTitle: "Session statistics",
    statsHours: "Hours completed",
    statsSessions: "Sessions completed",
    statsTypes: "Training types",
    viewToggleLabel: "Calendar view",
    viewMonth: "Month",
    viewWeek: "Week",
    monthLegendSessions: "Sessions",
    monthLegendChangePending: "Change awaiting client",
    monthLegendBlocked: "Blocked time",
    prevMonth: "Previous month",
    nextMonth: "Next month",
    legend: {
      title: "Legend",
      workingHoursAt: (place: string) => `Working hours: ${place}`,
    },
  },

  timeBlock: {
    sectionTitle: "Block slot",
    intro: "Quickly mark yourself unavailable — e.g. a dentist appointment. New bookings won't be possible during this window; already-booked sessions are unaffected.",
    addButton: "Block slot",
    dateLabel: "Date",
    startLabel: "From",
    endLabel: "To",
    durationLabel: "For how long?",
    customDuration: "Custom time",
    durationPreset: (n: number) =>
      n < 60 ? `${n} min` : n % 60 === 0 ? `${n / 60}h` : `${Math.floor(n / 60)}h ${n % 60}m`,
    reasonLabel: "Reason (optional)",
    reasonHint: "Visible only to you.",
    reasonPlaceholder: "e.g. Dentist",
    save: "Block",
    saving: "Saving...",
    cancel: "Cancel",
    close: "Close",
    listEmpty: "No blocked time windows.",
    defaultReason: "Unavailable",
    deleteLabel: "Remove block",
    deleteConfirmTitle: "Remove this block?",
    deleteConfirmMessage: "This time will be bookable again.",
  },

  clientStats: {
    sectionTitle: "Your statistics",
    monthTitle: "Trainings this month",
    monthBooked: "Booked",
    monthCompleted: "Completed",
    monthPending: "Pending",
    summaryTitle: "Summary",
    totalTrainings: "Trainings total",
    totalHours: "Training hours",
    hoursValue: (n: number) => `${n.toLocaleString("en-US", { maximumFractionDigits: 1 })} h`,
    sessionsCountValue: (n: number) => `${n} session${n === 1 ? "" : "s"}`,
    breakdownTitle: "Breakdown by training type",
    breakdownEmpty: "No completed trainings yet.",
    columnType: "Type",
    columnCount: "Count",
    columnHours: "Hours",
    trainersTitle: "Trainers you've trained with",
    trainersEmpty: "You haven't trained with any trainer yet.",
    messageButton: "Message",
    conversationWith: (name: string) => `Conversation with ${name}`,
  },

  common: {
    noRatings: "No ratings",
    online: "Online",
    decimalSeparator: ".",
    reviewsCount: (n: number) => `${n} ${n === 1 ? "review" : "reviews"}`,
    book: "Book",
    viewProfile: "View profile",
    individual: "1-on-1",
    group: (booked: number, capacity: number) => `Group ${booked}/${capacity}`,
    groupMax: (capacity: number) => `Group up to ${capacity}`,
    nextSlot: (text: string) => `Next slot: ${text}`,
    removeFilter: "Remove filter",
    pln: "PLN",
    close: "Close",
  },

  levels: {
    poczatkujacy: "Beginner",
    sredni: "Intermediate",
    zaawansowany: "Advanced",
    wszystkie: "All levels",
  },

  amenities: {
    parking: "Parking",
    wifi: "Free Wi-Fi",
    showers: "Showers",
    lockers: "Changing room / lockers",
    pet_friendly: "Pet friendly",
    kids_friendly: "Kids welcome",
    air_conditioning: "Air conditioning",
    equipment_included: "Equipment included",
    wheelchair_accessible: "Wheelchair accessible",
    water_dispenser: "Water available",
  },

  time: {
    today: "today",
    tomorrow: "tomorrow",
    yesterday: "yesterday",
    daysAgo: (n: number) => `${n} days ago`,
    oneWeekAgo: "1 week ago",
    weeksAgo: (n: number) => `${n} weeks ago`,
    oneMonthAgo: "1 month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    weekdaysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
    weekDisplayOrder: [1, 2, 3, 4, 5, 6, 0],
    weekdayLettersMonFirst: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
    months: [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ],
    monthsShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
    day: "Day",
    month: "Month",
    year: "Year",
    hour: "Hour",
    minute: "Minute",
  },

  search: {
    homeBarActivityLabel: "What are you looking for?",
    homeBarWhereLabel: "Where?",
    searchPlaceholder: "E.g. swimming, tennis, strength training...",
    searchButton: "Find trainers",
    openSearch: "Open search",
    closeSearch: "Close search",
    suggestionsNoCity: "location to be arranged",
    resultsHeading: "Trainers",
    tocSearch: "Search",
    resultCount: (n: number) => `${n} ${n === 1 ? "trainer" : "trainers"}`,
    noResults: "No trainers match the selected criteria.",
    noResultsHint: "Change the location, check online sessions or remove filters.",
    clearFilters: "Clear filters",
    loadingMore: "Loading more trainers…",
    loadMore: "Show more trainers",
    fromPrice: (n: number) => `from ${n.toFixed(0)} PLN`,
    fromPriceDuration: (n: number, min: number) => `from ${n.toFixed(0)} PLN / ${min} min`,
    cityAndPlace: (cities: string[], places: string[]) => `${cities.join(", ")}${cities.length > 0 && places.length > 0 ? " • " : ""}${places.join(" · ")}`,
    matches: {
      show: (n: number) => `Show matches (${n})`,
      hide: "Hide matches",
      fieldLabel: {
        activityDescription: "Activity description",
        bio: "About",
        activityName: "Activity name",
        discipline: "Discipline",
        locationName: "Place",
        city: "City",
        street: "Street",
      },
    },
    filters: {
      level: "Level",
      format: "Format",
      price: "Price",
      rating: "Rating",
      availability: "Availability",
      clearAll: "Clear all",
      audience: "Who for",
      audienceAdults: "Adults",
      audienceChild: (age: number) => `Child: ${age} ${age === 1 ? "year" : "years"}`,
      formatOptions: { individual: "1-on-1", group: "Group" },
      ratingOptions: { "4.5": "4.5 and up", "4.0": "4.0 and up" },
      availabilityOptions: { dzis: "Today", jutro: "Tomorrow", tydzien: "This week" },
    },
    sort: {
      label: "Sort by",
      rating: "Top rated",
      next: "Soonest slot",
      priceAsc: "Price: low to high",
      priceDesc: "Price: high to low",
      distance: "Nearest",
    },
    locationPicker: {
      trigger: "Choose location",
      triggerChosen: "Selected location",
      geolocationDenied: "Couldn't get your location — pick a place manually on the map.",
      searchPlacePlaceholder: "Search city or address…",
      noPlaceMatches: "No results.",
      placeInputPlaceholder: "City, street, or zip code",
      dragHint: "Click on the map or drag the pin to adjust the location.",
      expandMap: "Expand map",
      collapseMap: "Collapse map",
      close: "Close",
      radiusLabel: "Search radius",
      radiusThisCity: "This city",
      radiusUnlimited: "No limit",
      radiusKm: (km: number) => `${km} km`,
      radiusTag: (km: number) => `Radius: ${km} km`,
      cityTag: (city: string) => `City: ${city}`,
      confirm: "Apply",
      clear: "Clear location",
      distanceAway: (km: number) => `${km.toFixed(1)} km`,
      multiplePlacesFound: (query: string) => `Found several places named "${query}" — pick one:`,
      backToSearch: "← Back",
      districtOfCity: (district, city) => `${district} — district of ${city}`,
      cityInState: (city, state) => `${city} (${state})`,
      searching: "Searching…",
    },
    noResultsInCity: (city: string) => `No trainers in ${city}.`,
    narrowedToCity: (city: string) => `Showing results in ${city}.`,
    widenTo50: "Widen search to 50 km",
    widenToUnlimited: "Search everywhere",
  },

  trainerProfile: {
    accountVerified: "Account verified",
    toc: {
      activities: "Activities & calendar",
      gallery: "Gallery",
      certs: "Certificates",
      bio: "About",
      groups: "Join a training group",
      reviews: "Reviews",
      location: "Location",
    },
    activitiesSub: "Pick an activity, then a slot",
    activitiesPlaceholder: "Your activities will appear here. Add them in the Offer, hours & places section.",
    activitiesPlaceholderCta: "Go to the offer",
    chooseTrainingLabel: "Choose your training",
    chooseTrainingOrLabel: "Or choose your training",
    backToProfile: "Back to trainer profile",
    step1Hint: "tap for details",
    showDetails: "See details",
    hideDetails: "Hide details",
    calendar: "Calendar",
    busySlot: "Busy",
    workingHoursLabel: "Training places & working hours",
    workingHoursEmpty: "The trainer hasn't listed working hours.",
    noActivities: "No active activities offered.",
    pickTrainingPrompt: "Choose a training to see available times.",
    pickTrainingOrGroupPrompt: "Choose a group or a training to see details and times.",
    noPhotos: "No photos in the gallery.",
    openPhoto: "View photo",
    closePhoto: "Close photo",
    previousPhoto: "Previous photo",
    nextPhoto: "Next photo",
    noCerts: "No certificates.",
    noBio: "The trainer hasn't added a bio yet.",
    stats: {
      experienceValue: (years: number) => `${years} yrs`,
      experience: "experience",
      sessionsCompleted: "sessions completed",
      languages: "languages",
    },
    reviewTabs: {
      coach: (n: number) => `About the trainer (${n})`,
      activity: (n: number) => `About the activity (${n})`,
    },
    noReviews: "No reviews in this category.",
    locationUnset: "Location not set",
    openInMaps: "Open in maps",
  },

  reviewForm: {
    sectionTitle: "Your review",
    rateCoach: "Rate the trainer",
    rateActivity: (name: string) => `Rate: ${name}`,
    ratingLabel: "Rating",
    textLabel: "Comment",
    textPlaceholder: "How did the session go?",
    submit: "Submit review",
    submitting: "Sending…",
    submitted: "Thanks for your review!",
    cancel: "Cancel",
    notSignedIn: "Sign in to leave a review.",
    noCompletedSessions: "You can review this trainer after attending a session with them.",
    alreadyReviewed: "This review has already been submitted.",
    notEligible: "You can only review sessions that actually took place.",
    invalidInput: "Invalid data.",
    emptyText: "Comment can't be empty.",
    textTooLong: "Comment is too long.",
    genericError: "Something went wrong. Please try again.",
  },

  booking: {
    showCalendar: "Book",
    hideCalendar: "Hide calendar",
    trainerLabel: "Trainer",
    placeLabel: "Meeting place",
    noSpecificPlace: "Location to be arranged with the trainer",
    openInGoogleMaps: "Open in Google Maps",
    hoursLabel: "Hours",
    pickSlotHint: "Pick a slot on the calendar above.",
    confirm: "Confirm",
    payAndBook: "Book and pay",
    summary: {
      duration: (min: number) => `Duration: ${min} min`,
      total: (price: string) => `To pay: ${price}`,
      cancellation:
        "Free cancellation up to 24 hours before the session. After that there is no refund, unless you agree on another time with the trainer.",
      cancellationExample:
        "Example: a Saturday 10:00 session can be cancelled with a refund until Friday 10:00.",
      stripe: "Payment is handled by Stripe.",
      moreInfo: "Additional information",
      trainingWith: "Training with:",
    },
    details: {
      title: "Training details",
      priceLabel: "Price",
      clientLabel: "Client",
      clientsLabel: "Clients",
      infoButton: "Show training details",
    },
    reserve: "Reserve",
    review: {
      title: "Review your booking",
      subtitle: "After you click “Book and pay” you will be taken to payment.",
      trainingLabel: "Training",
      whenLabel: "Date and time",
      placeLabel: "Place",
      durationLabel: "Duration",
      minutes: (n: number) => `${n} min`,
      attendeeLabel: "For",
      totalLabel: "To pay",
      cancellationTitle: "Cancellation policy",
      back: "Back",
    },
    confirming: "Booking…",
    cancel: "Cancel",
    notOfferedThatDay: "This activity isn't offered on that day.",
    outsideWorkingHours: "Slot is outside working hours.",
    holidayClosed: "The trainer doesn't take bookings on this day (public holiday).",
    slotTaken: "This slot is already taken.",
    notSignedIn: "You're not signed in.",
    loginToBook: "Sign in to finish booking. Your selected time will be kept.",
    emailNotVerified: "Verify your email to book a session — we sent a link to your inbox.",
    invalidInput: "Invalid data.",
    invalidStartTime: "Invalid start time.",
    activityNotFound: "Activity not found.",
    notBookable: "This activity isn't bookable right now.",
    cannotBookOwn: "You can't book your own sessions.",
    mustBeFuture: "The slot must be in the future.",
    stepMismatch: (min: number) => `Start time must align to a ${min} min step.`,
    slotNoLongerFree: "This slot is no longer free.",
    noOpenSlots: "No open slots in the coming days.",
    previousWeek: "Previous week",
    nextWeek: "Next week",
    pickDayAndTime: "Pick a day & start time",
    everyMinutes: (min: number) => `Every ${min} min`,
    closedDay: "Closed",
    freeSlots: (n: number) => `${n} free`,
    closedDayNote: "Not available that day — pick another.",
    legend: {
      bookable: "Bookable",
      busy: "Busy",
      group: "Group session",
    },
    change: "Change",
    closePicker: "Close",
    fixedActivityNotAdHoc: "This activity has fixed times — join one of the slots below.",
    sessionNotFound: "Session not found.",
    sessionAlreadyCancelled: "This session has already been cancelled.",
    groupNotFound: "Group not found.",
    groupNotEmpty: "Only a group with no members can be deleted.",
    groupNotEnabled: "Standing groups are not enabled for this account.",
    groupNotDraft: "This period has already been approved.",
    groupNoSessions: "The period has no sessions.",
    groupNoFutureSessions: "There are no upcoming sessions left in this period.",
    groupOverlap: "One of the dates overlaps another of your sessions.",
    groupPastDate: "One of the dates is in the past.",
    groupOverCapacity: "The number of members exceeds the group's capacity.",
    groupPaymentProcessing: "Your payment is being processed — refresh in a moment.",
    groupChargeNotPayable: "This charge can no longer be paid.",
    groupFull: "This group has no free places.",
    groupSessionLocked: "A standing-group session can't be cancelled or booked separately. Contact support.",
    absenceNotAllowed: "An absence can't be reported for this session.",
    sessionStarted: "This session has already started.",
    absenceAlreadyReported: "An absence for this session has already been reported.",
    absenceNotFound: "Absence report not found.",
    absenceNotOpen: "This report can no longer be changed.",
    makeupInPast: "The makeup session must be in the future.",
    makeupPastDeadline: "The makeup date is past the last allowed day (end of next month).",
    makeupNotFound: "Makeup session not found.",
    makeupNotScheduled: "This makeup session is no longer scheduled.",
    sessionNotCancellable: "This session cannot be cancelled (it has started, is already cancelled, or is not part of an approved month).",
    refundNotFound: "Refund not found.",
    refundNotRetryable: "Only a failed refund can be retried.",
    alreadyJoined: "You've already joined this session.",
    sessionFull: "No spots left in this session.",
    activityNotForAdults: "This training is for children. Choose a child to book it.",
    activityNotForChildren: "This training isn't open to children.",
    childOutOfRange: (minAge: number, maxAge: number) =>
      `The child's age on the day of the training is outside the ${minAge}–${maxAge} range.`,
    childNotFound: "Child not found.",
    join: "Join",
    joining: "Joining…",
    joined: "Joined",
    full: "Full",
    noUpcomingFixedSessions: "No upcoming sessions.",
    forbidden: "You don't have access to this booking.",
    notFound: "Booking not found.",
    notPending: "This booking is no longer awaiting a decision.",
    accept: "Accept",
    accepting: "Accepting…",
    decline: "Decline",
    declining: "Declining…",
    cancelBooking: "Cancel booking",
    cancelling: "Cancelling…",
    cancelConfirmTitle: "Cancel this booking?",
    cancelConfirmMessage: "The time slot will be released and this can't be undone.",
    cancelConfirmYes: "Yes, cancel",
    cancelConfirmNo: "No, keep it",
    checkPayment: "Check payment",
    checkingPayment: "Checking…",
    paymentStillWaiting: "We don't see the payment yet. If you've already paid, try again in a moment.",
    paymentCheckTooSoon: "The payment was just checked. Try again in a while.",
    paymentCheckCancelled: "The payment wasn't completed in time, so the booking was cancelled.",
    alreadyPaidTitle: "This training is already paid",
    alreadyPaidMessage:
      "Your payment went through, so the booking wasn't cancelled — it's now confirmed. If you still want to cancel, cancel it again and we'll refund you.",
    alreadyPaidOk: "Got it",
    needsActionTitle: "Bookings needing approval",
    noNeedsAction: "No bookings awaiting confirmation.",
    newBookingsTitle: "New bookings",
    markSeen: "Mark as seen",
    markingSeen: "Marking…",
    notAcceptingPayments: "This trainer doesn't have online payments set up yet.",
    paymentError: "Couldn't start the payment. Please try again.",
    serviceFeeLabel: "Service fee",
    commissionLabel: "Commission",
    commissionInclStripe: (stripeFee) => `incl. Stripe fee PLN ${stripeFee}`,
    payoutLabel: "Your payout",
    paymentSuccessBanner: "Payment received — your booking is confirmed.",
    paymentCancelledBanner: "Payment cancelled — the booking was not paid for.",
    status: {
      awaiting_payment: "Awaiting payment",
      pending: "Pending",
      confirmed: "Confirmed",
      cancelled: "Cancelled",
      completed: "Completed",
      no_show: "No-show",
    },
  },

  auth: {
    emailVerified: {
      confirmedTitle: "Account confirmed",
      confirmedBody: "Thank you! Your email address is confirmed and you're logged in.",
      alreadyTitle: "Account already confirmed",
      alreadyBody: "This email address was already confirmed. You can simply log in.",
      expiredTitle: "Link expired",
      expiredBody: "The confirmation link has expired. Please try again.",
      invalidTitle: "Invalid link",
      invalidBody: "This confirmation link is not valid. Please try again.",
      close: "OK",
    },
    login: "Log in",
    logout: "Log out",
    emailLabel: "Email",
    emailPlaceholder: "you@email.com",
    passwordLabel: "Password",
    confirmPasswordLabel: "Confirm password",
    loginButton: "Log in",
    loggingIn: "Logging in…",
    registerButton: "Sign up",
    registering: "Signing up…",
    backButton: "Back",
    googleButton: "Continue with Google",
    or: "or",
    noAccountLink: "Don't have an account? Sign up",
    haveAccountLink: "Already have an account? Log in",
    passwordMismatch: "Passwords don't match.",
    passwordHint: "At least 9 characters. A few words work well — spaces are allowed.",
    passwordTooShortError: "The password must be at least 9 characters.",
    passwordTooLongError: "The password can be at most 128 characters.",
    passwordCompromisedError: "This password has appeared in a data breach. Please choose another.",
    genericError: "Something went wrong. Please try again.",
    checkInboxTitle: "Check your inbox",
    checkInboxBody: (email: string) => `We sent a confirmation link to ${email}.`,
    close: "Close",
    forgotPasswordLink: "Forgot password?",
    sendResetLink: "Send reset link",
    sendingReset: "Sending…",
    resetSentTitle: "Check your inbox",
    resetSentBody: (email: string) => `If an account exists for ${email}, we sent a password reset link to it.`,
    captchaError: "Could not verify you are human. Please try again.",
    googleOnlyAccountError:
      "This account was created with Google — no password has been set yet. Sign in with Google, or click \"Forgot password?\" to set one.",
  },

  resetPasswordPage: {
    title: "Set a new password",
    newPasswordLabel: "New password",
    confirmPasswordLabel: "Confirm new password",
    submitButton: "Change password",
    submitting: "Saving…",
    invalidTokenError: "This reset link is invalid or has expired.",
    genericError: "Could not change your password. Please try again.",
    successTitle: "Password changed",
    successBody: "Your password has been changed. You can now log in.",
    goHomeButton: "Go to homepage",
  },

  trainerEdit: {
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    discardChanges: "Cancel and discard changes",
    delete: "Delete",
    optionsMenu: "Options",
    confirmDeleteTitle: "Confirm deletion",
    addSection: (label: string) => `Add section: ${label}`,
    needsProfileFirst: "Save your basic profile info on the left first.",
    genericError: "Something went wrong. Please try again.",
    preview: {
      title: "Profile preview",
      subtitle: "This is how clients see your published profile.",
      backToEdit: "Back to editing",
      liveTitle: "Public profile",
      liveSubtitle: "This is how clients see your profile right now.",
      viewBarPreview: "Preview profile",
      viewBarLive: "Public profile",
      notLiveAlert: "Your profile isn't public yet, so you can't view it. You can only preview it.",
      checkStatus: "Check profile status",
    },
    contact: {
      emailLabel: "Email",
    },
    slug: {
      label: "Profile address",
      hint: "Defaults to your name. After changing it, the old link stops working.",
      copy: "Copy address",
      copied: "Copied",
    },
    onboarding: {
      title: (remaining: number) => `${remaining} step${remaining === 1 ? "" : "s"} left to a visible profile`,
      subtitle: "Any order — do it now or spread it out over time.",
      emailTodo: "Confirm your email",
      emailDone: "Email confirmed",
      profileTodo: "Complete your profile",
      profileDone: "Profile complete",
      paymentsTodo: "Connect payments",
      paymentsDone: "Payments connected",
      termsTodo: "Accept the trainer terms",
      termsDone: "Trainer terms accepted",
      disabledTitle: "Account disabled",
      disabledNoReason: "Contact us to find out why.",
    },
    publish: {
      confirm: "Publish changes",
      publishing: "Publishing…",
      upToDate: "Everything published",
      missingAvatar: "profile photo",
      missingPhone: "phone number",
      missingEmail: "email address",
      missingBio: 'the "About me" section',
      missingActivity: "at least one activity",
      changedSectionsLabel: "Unpublished changes in:",
      discard: "Discard",
      discarding: "Discarding…",
      leaveTitle: "Unconfirmed changes",
      leaveMessage:
        "You have changes that aren't visible on your public profile yet. Click “Confirm changes” first, otherwise clients won't see them.",
      leaveConfirm: "Leave anyway",
      leaveStay: "Stay on page",
      discardTitle: "Discard changes?",
      discardMessage: (section: string) =>
        `This will remove your unpublished changes in "${section}" and restore the currently published version. This can't be undone.`,
      sections: {
        avatar: "Profile photo",
        bio: "About me",
        infoCards: "Info cards",
        certificates: "Certificates",
        gallery: "Gallery",
      },
    },
    core: {
      headlineLabel: "Headline",
      headlinePlaceholder: "e.g. Personal trainer and athletic preparation",
      photoLabel: "Profile photo",
      photoChange: "Change photo",
      photoAdd: "Add photo",
      repositionLabel: "Drag and zoom",
      cropModalTitle: "Frame your photo",
      cropZoomLabel: "Zoom",
      cropSave: "Save",
      cropCancel: "Cancel",
      avatarTooLarge: "The photo is still too large even after compression — pick a smaller file.",
      nameLabel: "Full name",
      phoneLabel: "Phone",
      phoneEdit: "Edit phone number",
      phoneConfirm: "Confirm phone number",
      phoneNotShared: "Not shown to clients — used only for the system to contact you (e.g. notifications).",
      confirmButton: "Confirm",
    },
    connectPayments: {
      sectionLabel: "Payments",
      intro:
        "Connect a Stripe account so clients can pay online for bookings and you get paid out directly to your own account.",
      statusNotConnected: "Not connected",
      statusPending: "Verification in progress",
      statusActive: "Active",
      statusRestricted: "Needs more info",
      connectButton: "Connect Stripe",
      continueSetupButton: "Finish setup",
      connecting: "Connecting…",
      dashboardButton: "Stripe account details",
      dashboardOpening: "Opening…",
      genericError: "Something went wrong. Please try again.",
      commission: {
        title: "Platform commission",
        rate: (percent: string) => `Your commission: ${percent}% of the session price. It includes Stripe's fee.`,
        ratePlusStripe: (percent: string) =>
          `Your commission: ${percent}% of the session price plus Stripe's fee (1 zł + 1.6% of the payment).`,
        rateZero: "Your commission: 0%. We cover Stripe's fee.",
        sourceAdmin: "This rate was set by an administrator.",
        sourceAuto: (trainees: number, turnoverZl: string) =>
          `The rate is recalculated automatically on the 1st of each month. Last month: ${trainees} trainees, turnover ${turnoverZl} zł.`,
        sourceAutoPending:
          "The rate is recalculated automatically on the 1st of each month, based on your trainees and turnover.",
        modeLabel: "How should the commission be charged?",
        modeDeducted: "Deduct from the session price",
        modeAdded: "Add to the session price",
        exampleDeducted: (client: string, trainer: string) =>
          `Example: a 100 zł session. The client pays ${client} zł, you receive ${trainer} zł.`,
        exampleAdded: (client: string, trainer: string) =>
          `Example: a 100 zł session. The client pays ${client} zł, you receive ${trainer} zł.`,
        appliesToNew: "Applies to new bookings. Paid bookings don't change.",
        saved: "Saved",
      },
    },
    bio: {
      sectionLabel: "About",
      bioLabel: "Bio",
      bioPlaceholder: "Tell people about your experience and approach to training…",
      yearsExperienceLabel: "Years of experience",
      sessionsCompletedLabel: "Sessions completed",
      languagesLabel: "Languages",
      languagesPlaceholder: "e.g. EN / PL (B2)",
      equipmentNoteLabel: "Equipment / notes",
      equipmentNotePlaceholder: "e.g. Mats included",
    },
    infoCards: {
      addCard: "Add info",
      pickPrompt: "Choose info type",
      presetExperience: "Years of experience",
      presetSessions: "Sessions completed",
      presetLanguages: "Languages",
      presetEquipment: "Equipment / notes",
      presetCustom: "Custom info",
      customLabelPlaceholder: "Label (optional)",
      customValuePlaceholder: "Value",
    },
    certificates: {
      sectionLabel: "Certificates",
      titleLabel: "Certificate name",
      metaLabel: "Additional info",
      metaPlaceholder: "e.g. Issued 2022",
      add: "Add certificate",
    },
    gallery: {
      sectionLabel: "Gallery",
      add: "Add photo",
      uploading: "Uploading…",
      editButton: "Edit gallery",
      dialogTitle: "Gallery",
      layoutLabel: "Layout",
      layouts: { hero: "Hero photo", equal: "Equal tiles", mosaic: "Mosaic", strips: "Strips" },
      dragHandle: "Drag to reorder",
      dragHint: "Drag photos to change their order.",
      close: "Close",
      empty: "No photos yet.",
    },
    activities: {
      sectionLabel: "Activities",
      add: "Add activity",
      edit: "Edit",
      confirmDelete: "Delete this activity? It'll disappear from your offer and can no longer be booked.",
      confirmDeleteTip: "Existing bookings for it will stay unchanged.",
      nameLabel: "Activity name",
      descriptionLabel: "Activity description (optional)",
      descriptionPlaceholder: "What this activity covers, who it's for…",
      sportLabel: "Discipline",
      sportSearchPlaceholder: "Search discipline…",
      sportNoMatches: "No results.",
      sportAddNew: (name: string) => `Add new discipline: "${name}"`,
      sportAdding: "Adding…",
      sportPending: "pending admin approval",
      sportRequired: "Pick a discipline from the list or add a new one.",
      needsTrainerProfile: "Create your trainer profile first.",
      locationLabel: "Scheduling hours and location",
      onlineLabel: "Online training",
      onlineHint: "No venue — clients find it regardless of their city.",
      noSpecificLocation: "No specific location (to be arranged with the client)",
      addNewLocation: "+ Add new training place",
      noLocationShort: "location to be arranged",
      durationLabel: "Duration (min)",
      priceLabel: "Price (PLN)",
      capacityLabel: "Capacity (1 = 1-on-1)",
      levelLabel: "Level",
      audienceLabel: "Who is it for",
      audienceAdults: "Adults",
      audienceChildren: "Children",
      childMinAgeLabel: "Age from",
      childMaxAgeLabel: "Age to",
      audienceBadge: (allowsAdults: boolean, minAge: number | null, maxAge: number | null) => {
        const parts: string[] = [];
        if (allowsAdults) parts.push("Adults");
        if (minAge !== null && maxAge !== null) parts.push(`Children ${minAge}–${maxAge}`);
        return parts.join(" + ");
      },
      stepMinutesLabel: "Booking time step",
      stepMinutesOption: (min: number) => (min < 60 ? `${min} min` : "1 hour"),
      daysShort: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      empty: "No activities offered.",
      gallery: (n: number) => `Gallery (${n})`,
      fixedToggleLabel: "Book ahead (fixed slot on the calendar)",
      fixedToggleHint:
        "The activity will appear on the calendar at the chosen time even before anyone's booked it — e.g. a recurring group class.",
      fixedStartTimeLabel: "Start time",
      fixedDaysLabel: "Days of the week",
      fixedDaysRequired: "Pick at least one day.",
      fixedBadge: "Booked ahead:",
      pause: {
        menuLabel: "More options",
        button: "Pause all activities",
        intro: "All your activities will disappear from search and booking for the chosen period.",
        modeLabel: "For how long?",
        modeRange: "Pick a date range",
        modeIndefinite: "Indefinitely (until resumed)",
        startLabel: "From",
        endLabel: "To",
        confirm: "Pause activities",
        activeRange: (start: string, end: string) => `Activities paused from ${start} to ${end}.`,
        activeIndefinite: "Activities paused indefinitely.",
        resume: "Resume activities",
      },
      validation: {
        locationRequired: "Choose a training place.",
        nameMinLength: "Name must be at least 2 characters.",
        durationPositive: "Duration must be greater than 0.",
        pricePositive: "Price must be greater than 0.",
        capacityMin: "Capacity must be at least 1.",
        invalidTime: "Invalid time.",
        stepInvalid: "Pick a time step from the list.",
        audienceRequired: "Choose who the session is for: adults, children or both.",
        childRangeIncomplete: "Enter both the minimum and maximum age.",
        childRangeOrder: "The minimum age can't be higher than the maximum.",
        childAgeBounds: "Children's age must be between 1 and 17.",
      },
    },
    workingHours: {
      sectionLabel: "Training places & working hours",
      add: "Add training place",
      placeNameLabel: "Place name",
      placeNamePlaceholder: "e.g. Crux Climbing Gym",
      cityLabel: "City",
      requiredHint: "Place name and city are required. All other place details are optional.",
      districtLabel: "District",
      districtPlaceholder: "e.g. Downtown",
      zipCodeLabel: "ZIP code",
      zipCodePlaceholder: "00-000",
      streetLabel: "Street",
      buildingNumberLabel: "Building no.",
      flatNumberLabel: "Unit no.",
      additionalInfoLabel: "Additional info (e.g. entrance, floor)",
      additionalInfoPlaceholder: "e.g. Courtyard entrance, 2nd floor, intercom 12",
      addressPicker: {
        dragHint: "Click on the map or drag the pin to pick an address.",
        selectedPrefix: "Selected",
        pinOptional: "The map pin is optional, but it helps clients find the place.",
        changedHint:
          "The map changed the highlighted fields. Check the address and the pin on the map, and confirm they point to the same place.",
        confirmAddress: "Confirm address",
        districtOfCity: (district, city) => `${district} — district of ${city}`,
        cityInState: (city, state) => `${city} (${state})`,
      },
      fromLabel: "From",
      toLabel: "To",
      daysLabel: "Days of the week",
      empty: "No working hours defined.",
      addSubsection: "Add working hours at this place",
      editPlace: "Edit place",
      confirmDelete: "Delete these working hours?",
      gallery: (n: number) => `Training place photos (${n})`,
      amenities: "Amenities",
      amenitiesSaved: "Saved",
      slotsCount: (n: number) => `${n} ${n === 1 ? "slot" : "slots"}`,
      placesLabel: "Training places",
      deletePlace: "Delete place",
      confirmDeletePlace: "Remove this place from your list?",
      noHoursYet: "No working hours at this place yet.",
      hoursLabel: "Working hours",
      diagram: {
        title: "Weekly working schedule",
        overlap: "Stripes: working hours overlap at several places",
        draft: "Unsaved changes",
        newPlace: "New place",
        empty: "Pick days and hours and they'll show up here.",
      },
    },
  },

  home: {
    toc: {
      disciplines: "Disciplines",
      steps: "How it works",
      movement: "Movement",
      goodTrainer: "A good trainer",
      recommended: "Top trainers",
      trainerCta: "For trainers",
    },
    trainerCta: {
      title: "Are you a trainer? Get found.",
      text: "Show your offer to people looking for a trainer in your discipline. Create a profile, add your available slots and let new clients find you.",
      cta: "Create a trainer account",
      imageAlt: "Trainer wearing a cap",
    },
    movement: {
      title: "Movement you want to come back to",
      text: "Sport can be a goal, a challenge, a way to get fitter or simply a moment for yourself. A good trainer helps you find the right rhythm and enjoy training more.",
      features: [
        { title: "You're not alone", text: "Someone is beside you, supporting you at every stage of the way." },
        { title: "See your progress", text: "Training is matched to your goal and your abilities." },
        { title: "Enjoy moving", text: "It's easier to stay motivated when training feels rewarding." },
      ],
      imageAlt: "Smiling runner by the river",
    },
    goodTrainer: {
      title: "A good trainer is by your side",
      text: "They guide, correct and motivate you, and help you find the best path to your goal. They adapt training to you — your level, abilities and pace.",
      points: [
        "Training matched to your goals",
        "Support and motivation",
        "Help with developing technique",
        "A plan matched to your pace",
      ],
      cta: "Find your trainer",
      imageAlt: "A trainer helping a client with a kettlebell exercise",
    },
    steps: {
      title: "Find. Book. Train.",
      items: [
        { title: "Find a trainer", text: "Pick a discipline, a location and the person who fits your needs." },
        { title: "Choose a time", text: "Check availability and book a session." },
        { title: "Train your way", text: "Get your trainer's support, grow and enjoy moving." },
      ],
    },
    hero: {
      badge: "The training platform for people on the move",
      titleLine1: "Train smart.",
      titleLine2: "Achieve more.",
      subtitle: "Find a trainer matched to your goals and book a session in a few clicks.",
      cta: "Find a trainer",
      secondaryCta: "How it works",
      trust: {
        verified: "Verified trainers",
        payments: "Secure payments",
        flexible: "Flexible scheduling",
      },
      showcase: {
        trainingCaption: "Training",
        trainingSub: "energy and consistency",
        goalCaption: "Goal",
        goalSub: "plan and follow-through",
        trainerCardLabel: "Trainer card",
        trainerName: "Michael Newman",
        trainerSpecialty: "Personal training · Krakow",
        trainerRating: "4.9",
        trainerReviews: "128 reviews",
        trainerTag1: "Fat loss",
        trainerTag2: "Strength",
        studentCardLabel: "Student card",
        studentGreeting: "Hi, Anna!",
        studentGoal: "Goal: improve fitness",
        studentProgressLabel: "to goal",
      },
    },
    searchCard: {
      sportLabel: "What sport are you looking for?",
      sportPlaceholder: "Pick a sport",
      cityLabel: "Where do you train?",
      cityPlaceholder: "Enter a city",
    },
    recommended: {
      title: "Find your trainer",
      viewAll: "View all",
      empty: "No recommended trainers.",
    },
    calendar: {
      title: "Your calendar",
      empty: "No upcoming bookings.",
      withTrainer: "with",
      allBookings: "All bookings",
    },
  },

  disciplines: {
    title: "What do you want to train?",
    listLabel: "Disciplines",
  },

  howItWorks: {
    pill: "Simple process",
    titlePrefix: "How does",
    titleEmphasis: "GoForCoach work?",
    lead: "From setting a goal to your first session. Simple, clear, no back-and-forth in your inbox.",
    steps: [
      { title: "Set a goal", text: "Choose whether you want to get fitter, lose weight, build strength, or get back into activity." },
      { title: "Compare trainers", text: "Check specialties, reviews, pricing, location and available slots." },
      { title: "Book a slot", text: "Pick a single session or a package and book in a few clicks." },
      { title: "Track progress", text: "Monitor your activity, streak, goals and achievements from your dashboard." },
    ],
    highlights: [
      { title: "The right fit", text: "Filter by sport, goal, city, price and level." },
      { title: "Trust", text: "Trainer profiles show experience, reviews and verified info." },
      { title: "Motivation", text: "Your dashboard supports consistency, progress and good habits." },
    ],
    cta: {
      label: "Ready?",
      title: "Pick a trainer and get moving.",
      text: "Your first session could be the start of a new rhythm.",
      button: "Find a trainer",
    },
  },

  forCoach: {
    toc: {
      benefits: "Benefits",
      profile: "Your profile",
      steps: "How it works",
      pricing: "Pricing",
      mission: "Mission",
      faq: "FAQ",
      join: "Join",
    },
    metaTitle: "For trainers — GoForCoach",
    eyebrow: "GOFORCOACH FOR TRAINERS",
    titleLine: "You train others?",
    titleAccent: "Get found.",
    lead: "GoForCoach helps people looking for a trainer find you. You show your offer, and you manage bookings and payouts in one place.",
    support: { strong: "Focus on training.", rest: " We make the path to a booking easier." },
    cta: "Create a trainer account",
    micro: { strong: "Account and profile: 0 PLN.", rest: " You only pay for sessions that take place." },
    registerIntro: "To set up a trainer profile, create an account first — it only takes a minute.",
    heroAlt: "A trainer coaching a client during a session",
    benefits: [
      {
        title: "Be where people look for a trainer",
        text: "Your offer reaches people nearby who are actively looking for training.",
      },
      {
        title: "Less back-and-forth. More training.",
        text: "Interested people see your open slots, pick the one that suits them and book.",
      },
      {
        title: "Show how you train",
        text: "Your profile, offer and experience help people quickly see whether you're the right trainer.",
      },
    ],
    profile: {
      eyebrow: "YOUR PROFILE",
      title: "Your profile works for your brand.",
      text: "Show your specialities, experience, reviews, locations and open slots. Build a profile that attracts people looking for a trainer just like you.",
      devicesLabel: "Sample trainer profile on a laptop and a phone",
    },
    link: {
      kicker: "YOUR TRAINER LINK",
      titleLine: "Already have people who train with you?",
      titleAccent: "Share your trainer link with them.",
      text: "Every trainer can send a direct link to their profile. The client picks a session and a time, and the booking lands in your calendar.",
      loginNote: { strong: "After you sign in,", rest: " you'll find your personal link on your trainer profile." },
    },
    steps: {
      eyebrow: "HOW IT WORKS",
      title: "4 steps and you're set.",
      items: [
        { title: "Create an account", text: "A few minutes and you can start building your profile." },
        { title: "Show your offer", text: "Add specialities, training places, prices and open slots." },
        { title: "Connect Stripe", text: "A free Stripe account handles payments and payouts." },
        {
          title: "Accept bookings",
          text: "People interested in training pick a time, and the booking lands in your calendar.",
        },
      ],
    },
    pricing: {
      eyebrow: "SIMPLE RULES",
      title: "Simple, fair rules.",
      cards: [
        { big: "0 PLN", label: "account and profile", text: "Creating an account and keeping a trainer profile is free." },
        {
          big: "5%",
          label: "per completed session",
          text: "You only pay for sessions that take place. The more you complete in a month, the lower the commission.",
        },
        {
          big: "You decide",
          label: "prices and availability",
          text: "You decide what your sessions cost, where they take place and when you're available.",
        },
      ],
    },
    mission: {
      eyebrow: "OUR MISSION",
      title: "We connect people who want to train with those who can lead them.",
      text: "We build GoForCoach together with the trainers who have been with us from the start.",
      imgAlt: "An active person training outdoors",
    },
    faq: {
      eyebrow: "FREQUENTLY ASKED QUESTIONS",
      title: "Questions? We have answers.",
      intro: "Can't find your answer here? Check the full GoForCoach Help page.",
      moreLink: "See the full Help page",
    },
    final: {
      title: "Get found. Start accepting bookings.",
      text: "Show your offer, share your profile and make the first step toward training with you easy.",
      cta: "Create a trainer account",
      imgAlt: "An active person outdoors",
    },
  },

  chat: {
    title: "Chat",
    withTrainer: "with",
    placeholder: "Write a message…",
    send: "Send",
    empty: "No messages yet. Write the first one.",
    notSignedIn: "You're not signed in.",
    notFound: "Chat not found.",
    forbidden: "You don't have access to this chat.",
    invalidInput: "Invalid data.",
    emptyMessage: "Message can't be empty.",
    messageTooLong: "Message is too long.",
    sendMessageButton: "Send a message",
    backToProfile: "Back to profile",
    emojiPicker: "Insert emoji",
    filterGeneral: "General",
    durationMinutes: (n: number) => `${n} min`,
    loadingOlder: "Loading earlier messages…",
    unread: "New message",
    unpaid: "Unpaid",
  },

  messagesInbox: {
    title: "Messages",
    empty: "No conversations yet.",
  },

  reschedule: {
    options: "Session options",
    changeTime: "Change time",
    dateLabel: "Date",
    startLabel: "Start time",
    save: "Save new time",
    saving: "Saving…",
    cancel: "Cancel",
    clientsLabel: "Clients",
    noClients: "Nobody has booked this session yet.",
    affectedClients: (n: number) =>
      n === 1 ? "The client will be notified of the change." : `${n} clients will be notified of the change.`,
    seenBadge: "Saw the change",
    notSeenBadge: "Not seen yet",
    bannerTitle: (activity: string, trainerName: string) => `Trainer ${trainerName} changed the time: ${activity}`,
    bannerBody: (oldText: string, newText: string) => `Was: ${oldText}. Now: ${newText}.`,
    was: (oldText: string) => `Was: ${oldText}`,
    gotIt: "Got it",
    acknowledging: "Saving…",
    messageTrainer: "Message trainer",
    changedChip: "Time changed",
  },

  cancelSession: {
    menuItem: "Cancel session",
    title: "Cancel this session?",
    message: (n: number) =>
      n === 0
        ? "The session will be removed from your calendar."
        : n === 1
          ? "The client will be notified and the amount they paid will be refunded."
          : `${n} clients will be notified and the amounts they paid will be refunded.`,
    clientRefund: (name: string, amount: string) => `${name} — refund ${amount} PLN`,
    clientUnpaid: (name: string) => `${name} — unpaid, no refund`,
    whoPaysTitle: "Who bears the refund cost",
    whoPays: [
      "The client gets back the full amount they paid.",
      "Your share of the payment (price minus the platform fee) is deducted from your Stripe account.",
      "The platform refunds its fee. Its only cost is the payment processor fee, which Stripe does not refund.",
    ],
    ifFails:
      "If the refund fails (e.g. insufficient funds on your Stripe account), the session is still cancelled, the client is informed, and our team will finish the refund manually.",
    confirm: "Cancel session",
    back: "Go back",
    cancelledTitle: "Session cancelled",
    closeButton: "Close",
    freesTimeHint:
      "Cancelling frees this time in your calendar — it can be booked again. If you want to keep this time blocked, add it under “Block slot”.",
    refundFailedNotice: (n: number) =>
      n === 1
        ? "Session cancelled, but 1 refund failed. Our team will finish it manually."
        : `Session cancelled, but ${n} refunds failed. Our team will finish them manually.`,
  },

  notifications: {
    title: "Notifications",
    empty: "No notifications.",
    markAllRead: "Mark all as read",
    detail: {
      open: "Go to it",
      close: "Close",
      finished: "Session finished",
      inProgress: "Session in progress",
      startsIn: (minutes: number) => `In ${minutes} min`,
      rescheduled: (from: string, to: string) => `Time changed from ${from} to ${to}`,
    },
    sections: {
      new: "New",
      today: "Today",
      earlier: "Earlier",
    },
    types: {
      new_message: (from: string) => `New message from ${from}`,
      today_session: "You have a session today",
      upcoming_session: "Session in under 2 hours",
      booking_requires_action: (clientName: string) => `New booking from ${clientName}`,
      booking_confirmed: (trainerName: string) => `Your booking with ${trainerName} is confirmed`,
      booking_accepted: (trainerName: string) => `Trainer ${trainerName} confirmed your booking`,
      booking_declined: (trainerName: string) => `Trainer ${trainerName} declined your booking`,
      booking_cancelled: (clientName: string) => `${clientName} cancelled the booking`,
      session_rescheduled: (trainerName: string) => `Trainer ${trainerName} changed the time of your session`,
      session_cancelled: (trainerName: string, refund: "none" | "refunded" | "failed") =>
        refund === "refunded"
          ? `Trainer ${trainerName} cancelled the session. Your refund is on its way.`
          : refund === "failed"
            ? `Trainer ${trainerName} cancelled the session. Your refund is being processed.`
            : `Trainer ${trainerName} cancelled the session.`,
      booking_expired: (trainerName: string) =>
        `Your booking with ${trainerName} expired because it was not paid in time`,
      commission_changed: (oldPercent: string, newPercent: string) =>
        `Your commission changed from ${oldPercent}% to ${newPercent}%`,
      group_period_draft_ready: (groupName: string, month: string) =>
        `The schedule for ${month} in group “${groupName}” is ready for approval`,
      group_period_approved: (groupName: string, month: string) =>
        `New month in group “${groupName}”: ${month} — pay in the panel`,
      group_payment_reminder: (groupName: string, month: string) =>
        `Payment for ${month} in group “${groupName}” is due soon`,
      group_payment_confirmed: (groupName: string, month: string) =>
        `Payment for ${month} in group “${groupName}” received`,
      group_charge_expired: (groupName: string, month: string) =>
        `${month} was not paid — your spot in group “${groupName}” was released`,
      group_absence_reported: (groupName: string, memberName: string) =>
        `${memberName} reported an absence in group “${groupName}” — schedule a makeup`,
      group_makeup_assigned: (groupName: string) => `The trainer scheduled your makeup session in group “${groupName}”`,
      group_makeup_cancelled: (groupName: string) =>
        `The trainer cancelled your makeup session in group “${groupName}” — they may set a new date`,
      group_session_cancelled: (groupName: string, refund?: string, unpaid?: "pending" | "voided") =>
        `The trainer cancelled a session in group “${groupName}”` +
        (unpaid === "voided"
          ? " — you do not need to pay for this month"
          : unpaid === "pending"
            ? " — pay for the month and we will refund the share for this session"
            : refund
              ? ` — your ${refund} refund is being processed`
              : ""),
    },
    push: {
      new_message: (senderName: string) => ({
        title: "New message",
        body: `${senderName} sent you a message`,
      }),
      booking_requires_action: (clientName: string) => ({
        title: "New booking",
        body: `${clientName} booked a session with you`,
      }),
      booking_confirmed: (trainerName: string) => ({
        title: "Booking confirmed",
        body: `Your session with ${trainerName} is confirmed`,
      }),
      booking_accepted: (trainerName: string) => ({
        title: "Booking confirmed",
        body: `Trainer ${trainerName} confirmed your session`,
      }),
      booking_declined: (trainerName: string) => ({
        title: "Booking declined",
        body: `Trainer ${trainerName} can't take this booking`,
      }),
      booking_cancelled: (clientName: string) => ({
        title: "Booking cancelled",
        body: `${clientName} cancelled the booking`,
      }),
      session_rescheduled: (trainerName: string) => ({
        title: "Session time changed",
        body: `Trainer ${trainerName} changed the time of your session`,
      }),
      session_cancelled: (trainerName: string) => ({
        title: "Session cancelled",
        body: `Trainer ${trainerName} cancelled your session`,
      }),
      booking_expired: (trainerName: string) => ({
        title: "Booking expired",
        body: `Your booking with ${trainerName} was not paid in time`,
      }),
    },
  },

  push: {
    notSignedIn: "You're not signed in.",
    trainerReason: "Get notified on your phone or computer when a client books a slot.",
    clientReason: "Get notified about your booking confirmations and updates.",
    afterMessageReason: "Turn on push notifications so you don't miss the reply.",
    afterBookingReason: "Turn on push notifications so you don't miss booking updates.",
    enable: "Enable notifications",
    enabling: "Enabling…",
    error: "Something went wrong. Check your browser settings and try again.",
    settings: {
      enableReason: "To receive push notifications, enable them in this browser.",
      messages: "Text messages",
      upcomingTrainings: "Upcoming sessions",
      newBookingRequests: "New bookings",
      emailNotifications: "Email notifications",
      smsNotifications: "SMS notifications",
      on: "On",
      off: "Off",
      saveError: "Couldn't save settings. Please try again.",
      subscribed: "Push notifications are enabled in this browser.",
      disable: "Disable",
      disabling: "Disabling…",
      blocked: "You've blocked notifications in your browser settings — enable them there to receive them here.",
      unsupported: "This browser doesn't support push notifications.",
    },
  },

  location: {
    settings: {
      title: "Location access",
      description: "Let your browser share your location, to show trainers near you.",
      granted: "Location access is enabled in this browser.",
      prompt: "Enable location access in this browser.",
      blocked: "You've blocked location access in your browser settings — enable it there to use this.",
      unsupported: "This browser doesn't support geolocation.",
      enable: "Enable access",
      enabling: "Enabling…",
      error: "Something went wrong. Check your browser settings and try again.",
      autoDetectLabel: "Automatic location detection",
      autoDetectHint:
        "When off, the app won't ask for or use your location — search stays set manually. Your browser permission is unaffected, this only switches our own use of it.",
      on: "On",
      off: "Off",
      saveError: "Couldn't save settings. Please try again.",
    },
  },

  userMenu: {
    settings: "Settings",
    billing: "Billing",
  },

  billingPage: {
    title: "Billing",
    subtitle: "Completed trainings grouped by month. Select the ones you want to bill.",
    empty: "You have no completed trainings to bill yet.",
    emptyInRange: "No completed trainings in the selected period.",
    filter: {
      from: "From",
      to: "To",
      apply: "Show",
      reset: "Current and previous month",
      defaultHint: "By default we show the current and previous month.",
    },
    monthTotal: "Month total",
    selectMonth: "Select whole month",
    selectRow: (client: string) => `Select training with ${client}`,
    selectedCount: (n: number) => `Selected: ${n}`,
    selectedTotal: "Selected total",
    generateInvoice: "Generate invoice",
    generateInvoiceSoon: "Invoice generation is coming soon",
    columns: {
      date: "Date",
      activity: "Activity",
      location: "Location",
      client: "Client",
      price: "Price",
      payment: "Payment",
    },
    payment: {
      paid: "Paid",
      unpaid: "Unpaid",
      refunded: "Refunded",
    },
  },

  settingsPage: {
    title: "Settings",
    profileNav: "Profile data",
    notificationsNav: "Notifications",
    notificationsTitle: "Notifications",
    notificationsDescription: "Choose what you want to be notified about and how.",
    locationNav: "Location",
    passwordNav: "Password",
    accountNav: "Account",
    childrenNav: "Children",
  },

  profileData: {
    title: "Profile data",
    description: "Display name, email and gender.",
    nameLabel: "Full name or nickname",
    nameHint: "Visible to others, e.g. in chats and reviews.",
    emailLabel: "Email address",
    emailHint: "Changing the email address isn't available yet.",
    genderLabel: "Gender",
    genderNone: "Not selected",
    genderFemale: "Female",
    genderMale: "Male",
    genderOther: "Other",
    genderUndisclosed: "Prefer not to say",
    phoneLabel: "Phone number",
    phoneHint: "Not visible to others. Used only for SMS about cancelled or rescheduled sessions and reminders.",
    phoneInvalidError: "Enter a valid phone number, e.g. 600 123 456.",
    saveButton: "Save",
    saving: "Saving...",
    success: "Your details were saved.",
    nameRequiredError: "Enter a full name or nickname.",
    nameTooLongError: "The name can be at most 80 characters.",
    genericError: "Couldn't save your details. Please try again.",
  },

  changePassword: {
    title: "Password",
    changeDescription: "Change your account password. Your other devices will be signed out.",
    googleOnlyNote:
      "You sign in with Google, so changing the password isn't available for this account.",
    currentPasswordLabel: "Current password",
    newPasswordLabel: "New password",
    confirmPasswordLabel: "Repeat new password",
    changeButton: "Change password",
    currentRequiredError: "Enter your current password.",
    tooShortError: "The new password must be at least 9 characters.",
    tooLongError: "The password can be at most 128 characters.",
    wrongCurrentError: "The current password is incorrect.",
    saving: "Saving...",
    success: "Password saved.",
    sameAsCurrentError: "The new password must differ from the current one.",
    genericError: "Couldn't save the password. Please try again.",
  },

  accountDeletion: {
    title: "Delete account",
    description:
      "Deleting your account is permanent. Your profile, messages, and all trainings will be permanently removed.",
    deleteButton: "Delete account",
    retentionNote:
      "We keep only the billing data of bookings and payments, and your email address, for as long as the law requires (up to 6 years) — without your profile data or messages.",
    pauseHint: "Instead of deleting your account, you can pause your activities for a while — your profile and data are kept.",
    pauseLink: "Pause activities",
    confirmTitle: "Permanently delete your account?",
    confirmMessage:
      "This will permanently delete your profile, all chats, and all trainings. This cannot be undone.",
    confirmTip: "Tell us why you're deleting your account — it helps us improve.",
    confirmButton: "Permanently delete account",
    deleting: "Deleting...",
    reasonLabel: "Reason for deleting your account",
    reasonPlaceholder: "Why are you deleting your account?",
    reasonRequired: "Please provide a reason for deleting your account.",
    genericError: "Could not delete your account. Please try again.",
    refundsPending: "We can't delete your account right now: a refund for cancelled sessions is still in progress. Please try again in a few hours.",
  },

  language: {
    switchTo: (name: string) => `Switch to ${name}`,
    polish: "Polish",
    english: "English",
  },

  footer: {
    tagline: "Find a trainer that matches you and train the way you like.",
    links: {
      findTrainer: "Find a trainer",
      howItWorks: "How it works",
      forCoach: "For trainers",
      contact: "Contact",
      help: "Help",
      terms: "Terms",
      trainerTerms: "Trainer terms",
      privacy: "Privacy policy",
      cookies: "Cookies",
      cookieSettings: "Cookie settings",
    },
    social: {
      instagram: "Instagram",
      facebook: "Facebook",
    },
    copyright: (year: number) => `© ${year} GoForCoach. All rights reserved.`,
  },

  legalPage: {
    comingSoon: "This page is coming soon.",
    titles: {
      contact: "Contact",
      help: "Help",
      terms: "Terms",
      trainerTerms: "Terms for Trainers",
      privacy: "Privacy policy",
      cookies: "Cookies",
    },
  },

  contactPage: {
    intro: "Have a question about a booking, a payment or your account? Write to us.",
    responseNote: "We will reply to the e-mail address you provide.",
    emailLine: "You can also write directly to",
    supportEmail: "support@goforcoach.com",
    nameLabel: "Name",
    emailLabel: "E-mail",
    subjectLabel: "Subject",
    subjectOptions: [
      { value: "booking", label: "Booking" },
      { value: "payment", label: "Payment or refund" },
      { value: "trainer", label: "Trainer account" },
      { value: "account", label: "My account" },
      { value: "other", label: "Other" },
    ],
    messageLabel: "Message",
    messagePlaceholder: "Describe the matter. Do not include your password or full card details.",
    send: "Send message",
    sending: "Sending…",
    successTitle: "Message sent",
    successBody: "Thank you. We will reply to the e-mail address you provided.",
    errors: {
      required: "Please fill in all fields.",
      email: "Enter a valid e-mail address.",
      tooLong: "The message is too long.",
      rateLimited: "Too many messages sent. Please try again in a few minutes.",
      captcha: "Could not verify you are human. Please try again.",
      generic: "Could not send the message. Try again or write to support@goforcoach.com.",
    },
  },

  errorPages: {
    notFoundTitle: "We could not find this page",
    notFoundBody: "The address may be outdated or the page may have moved.",
    errorTitle: "Something went wrong",
    errorBody: "Please try again in a moment. If the problem persists, contact us.",
    retry: "Try again",
    backHome: "Back to the home page",
    findTrainer: "Find a trainer",
  },

  helpPage: {
    intro:
      "Find answers about choosing a trainer, bookings, payments and your account. If you need help, write to support@goforcoach.com.",
    sections: [
      {
        heading: "Looking for a trainer",
        items: [
          {
            q: "What is GoForCoach?",
            a: "GoForCoach connects people looking for training with trainers across many disciplines. Compare offers by location, level and goal, pick a trainer and book a session online.",
          },
          {
            q: "Is GoForCoach free to use?",
            a: "Creating a client account and browsing offers is free. You pay for the session or package you choose, at the price shown when you book.",
          },
          {
            q: "How do I find and choose a trainer?",
            a: "Pick a discipline and location in the search. Compare experience, specialisations, prices, availability and client reviews. Check that the offer fits your goal and level.",
          },
          {
            q: "Can I train as a beginner?",
            a: "Yes. Check the level of the class in the offer and choose a beginner session. If you are unsure, message the trainer in the GoForCoach chat.",
          },
          {
            q: "Are there sessions for children?",
            a: "Yes. Trainers state whether a class is for adults, for children and for which ages. In search, use the \"Who for\" filter and the child's age. Save your children in your account settings — you book from your own account and pick the child when booking.",
          },
          {
            q: "Can I train online?",
            a: "Yes, GoForCoach also offers online sessions. Choose a trainer who offers this format and check the details of their offer.",
          },
          {
            q: "I can't find a trainer in my area or discipline. What now?",
            a: "Try changing the location or look at online sessions. You can also write to support@goforcoach.com with the city and discipline you are looking for.",
          },
          {
            q: "How do I contact a trainer?",
            a: "Message the trainer in the GoForCoach chat. You can ask about the offer, the venue and agree the details of the session.",
          },
        ],
      },
      {
        heading: "Bookings and payments",
        items: [
          {
            q: "How do I book a session?",
            a: "On the trainer's profile choose a class and an available time, check the booking details, then pay for the session through GoForCoach.",
          },
          {
            q: "Do I need an account to book?",
            a: "Yes. A free account lets you book sessions, contact the trainer and check your bookings.",
          },
          {
            q: "When and how do I pay for a session?",
            a: "You pay when you book. Payments are handled by Stripe. You will see the available methods and the full amount before confirming the payment.",
          },
          {
            q: "Who sets the price of a session?",
            a: "The trainer sets the price. Check the price, duration and scope of the service before booking.",
          },
          {
            q: "Where can I find my bookings and payments?",
            a: "Log in to your account to see your scheduled sessions and information about bookings and payments.",
          },
          {
            q: "Can I buy a session package or a subscription?",
            a: "Yes. Packages and automatically renewing subscriptions are available. Check the trainer's offer and the terms of the package or subscription before buying.",
          },
          {
            q: "What does automatic subscription renewal mean?",
            a: "A subscription renews automatically under the terms of the chosen offer. Before buying, check the renewal frequency, the price and how to end the subscription.",
          },
          {
            q: "Can I change the time of a session?",
            a: "Yes, agree a new time with the trainer in the GoForCoach chat. Write as soon as possible. The new time has to be agreed with the trainer.",
          },
          {
            q: "How late can I cancel a session and what about a refund?",
            a: "You can cancel a session up to 24 hours before it starts. Contact the trainer in the GoForCoach chat as soon as possible. If you cancel within this deadline you get a full refund of the amount paid for the session.",
          },
          {
            q: "What if I cancel less than 24 hours before the start?",
            a: "If you cancel later than 24 hours before the session starts, the payment is not refunded. You can still agree another time with the trainer in the GoForCoach chat.",
          },
          {
            q: "What if the trainer cancels the session?",
            a: "The trainer should tell you about the cancellation and agree with you whether you want a refund or a new time. Settle the details in the GoForCoach chat.",
          },
          {
            q: "What if the trainer doesn't show up?",
            a: "Message the trainer in the GoForCoach chat. If you can't sort it out, report it to support@goforcoach.com. Include details that let us identify the booking.",
          },
          {
            q: "My payment failed. What should I do?",
            a: "Check the booking status and whether your account was charged. If money was taken but you don't see a booking confirmation, write to support@goforcoach.com before paying again.",
          },
          {
            q: "Can I choose a different trainer?",
            a: "Yes, you can choose a different trainer for later sessions.",
          },
        ],
      },
      {
        heading: "For trainers",
        items: [
          {
            q: "How do I create a trainer profile?",
            a: "Create a free trainer account and complete your profile. Add a photo, experience, specialisations, offer, prices, venues and available times. You can also sign up with a Google account.",
          },
          {
            q: "Is a trainer account paid?",
            a: "No. Creating an account and a trainer profile is free.",
          },
          {
            q: "How does the commission work?",
            a: "The commission is 5% of each completed session. The more sessions you complete in a month, the lower it gets.",
          },
          {
            q: "How do I set prices and manage availability?",
            a: "In the trainer panel set the prices of your services and your available times. Keep your schedule up to date so clients can book during the hours you teach.",
          },
          {
            q: "Can I teach at several places or online?",
            a: "Yes. You can add several locations and offer online sessions. For each service, state clearly the place or the format.",
          },
          {
            q: "Can I offer packages and subscriptions?",
            a: "Yes, you can offer session packages and automatically renewing subscriptions. Describe the scope of the offer and its terms before making it available to clients.",
          },
          {
            q: "When do I get paid for a session?",
            a: "The trainer's earnings are paid out by Stripe after the session is held. This usually takes 1–3 days.",
          },
          {
            q: "Do I need a Stripe account?",
            a: "Yes. Stripe handles payments and payouts, and a Stripe account is required to activate your trainer account. Creating a Stripe account is free.",
          },
          {
            q: "What if a client cancels at the last minute?",
            a: "If a client cancels later than 24 hours before the session starts, the payment is not refunded. You can still agree another time in the GoForCoach chat.",
          },
          {
            q: "What should I do if I have to cancel a session?",
            a: "Tell the client through GoForCoach as soon as possible. Agree with them whether they want a refund or a new time.",
          },
          {
            q: "How do I get more bookings?",
            a: "Add a current photo and a concrete description of your experience. Explain who you help, and give prices, venues and available times. Keep your schedule up to date and reply to client messages.",
          },
        ],
      },
      {
        heading: "Account and help",
        items: [
          {
            q: "How can I log in?",
            a: "Log in with your account details or use Google sign-in.",
          },
          {
            q: "I forgot my password. What now?",
            a: "On the login screen choose the password recovery option and follow the instructions.",
          },
          {
            q: "How do I change my details or delete my account?",
            a: "You can change your details in the profile settings. If you need help deleting your account, write to support@goforcoach.com.",
          },
          {
            q: "Can I leave a review for a trainer?",
            a: "Yes. After a session you can leave a review. Describe your experience to help others choose a trainer.",
          },
          {
            q: "Where can I find information about my data?",
            a: "The rules for processing data and information about your rights are in the GoForCoach Privacy Policy.",
          },
          {
            q: "How do I report a problem or contact GoForCoach?",
            a: "Write to support@goforcoach.com. Describe the problem and include details that identify the booking, if it concerns one. Do not send your password or full payment card details.",
          },
        ],
      },
    ],
    stillNeedHelp: {
      heading: "Still need help",
      body: "Didn't find an answer? Write to us — we'll gladly help. Contact GoForCoach: support@goforcoach.com.",
    },
  },

  childProfiles: {
    title: "Children",
    description: "Save your children so you can book trainings for them from your own account.",
    empty: "No children added yet.",
    firstNameLabel: "Child's first name",
    birthDateLabel: "Date of birth",
    add: "Add child",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    edit: "Edit",
    archive: "Remove",
    archiveConfirm: (name: string) =>
      `Remove ${name}? Scheduled sessions stay as they are, but the child disappears from your list.`,
    ageYears: (n: number) => `${n} ${n === 1 ? "year" : "years"}`,
    attendeeLabel: (childName: string, age: number, parentName: string) =>
      `${childName} (${age}) — parent: ${parentName}`,
    errors: {
      notSignedIn: "Sign in to manage your children.",
      firstNameRequired: "Enter the child's first name.",
      birthDateInvalid: "Enter a valid date of birth.",
      birthDateFuture: "The date of birth can't be in the future.",
      mustBeUnder18: "The child must be under 18.",
      notFound: "Child not found.",
    },
    picker: {
      forWhom: "Who is it for?",
      forMe: "For me",
      forChild: "For my child",
      saveChild: "Save child",
      pickChild: "Choose a child",
      noChildren: "You haven't saved a child yet.",
      ageRange: (minAge: number, maxAge: number) => `ages ${minAge}–${maxAge}`,
      loginToAddChild: "Sign in to book a training for your child.",
    },
  },

  trainerTermsPage: {
    accept: "I accept the terms",
    accepting: "Saving…",
    acceptedOn: (date: string) => `Terms accepted on ${date}`,
    loginHint: "Log in as a trainer to accept the terms.",
    notTrainerHint: "You can accept the terms once you have created a trainer profile.",
    backToProfile: "Back to trainer profile",
    error: "Could not save your acceptance. Please try again.",
  },

  cookieConsent: {
    title: "Cookie settings",
    description:
      "We use cookies to make the app work, and — with your consent — to help us understand how you use it so we can improve your experience.",
    acceptAll: "Accept all",
    rejectNonEssential: "Reject non-essential",
    customize: "Customize",
    savePreferences: "Save preferences",
    categories: {
      necessary: {
        name: "Necessary",
        description:
          "Keep you logged in and remember your chosen language. These can't be turned off — the app doesn't work without them.",
      },
      analytics: {
        name: "Analytics",
        description:
          "Helps us understand how you use the app so we can improve your experience. Optional.",
      },
    },
  },

  cookiePolicy: {
    intro: "Below is the full list of cookies GoForCoach uses and what each one is for.",
    manageButton: "Manage cookie settings",
    table: {
      nameHeader: "Name",
      purposeHeader: "Purpose",
      durationHeader: "Duration",
      categoryHeader: "Category",
    },
    categoryLabels: {
      necessary: "Necessary",
      analytics: "Analytics",
    },
    rows: [
      {
        name: "better-auth.session_token and related",
        purpose: "Keeps you logged in.",
        duration: "7 days",
        category: "necessary",
      },
      {
        name: "locale",
        purpose: "Remembers your chosen app language.",
        duration: "1 year",
        category: "necessary",
      },
      {
        name: "analytics_consent",
        purpose: "Remembers your analytics consent choice.",
        duration: "1 year",
        category: "necessary",
      },
      {
        name: "cc_cookie",
        purpose: "Remembers the content of the cookie consent choice you're reading about right now.",
        duration: "182 days",
        category: "necessary",
      },
      {
        name: "analytics identifier",
        purpose: "Helps us understand how you use the app so we can improve it. Only set once you consent.",
        duration: "up to 1 year",
        category: "analytics",
      },
    ],
  },
  groupAbsence: {
    groupLabel: "Group",
    makeupLabel: "Makeup",
    reportButton: "Report absence",
    confirmTitle: "Report an absence?",
    confirmMessage:
      "The trainer will be notified and will schedule a makeup session. You can undo the report until a date is set.",
    confirmYes: "Report absence",
    confirmNo: "Go back",
    reportedText: "You reported an absence — the trainer will schedule a makeup session.",
    withdrawButton: "Undo report",
    working: "One moment…",
    makeupScheduledText: (when: string) => `Makeup: ${when}.`,
    closedText: "Absence closed — the makeup deadline passed.",
    completedText: "Session made up.",
    notCompletedText: "The makeup session did not take place.",
    makeupSessionText: "This is a makeup session replacing an absence.",
  },
  groupAbsences: {
    summary: (open: number) => (open > 0 ? `Absences (${open} to handle)` : "Absences"),
    empty: "No absences.",
    missed: (when: string) => `Absence: ${when}`,
    deadline: (date: string) => `make up by ${date}`,
    statusReported: "Reported",
    statusMakeupScheduled: "Makeup scheduled",
    statusCompleted: "Made up",
    statusNotCompleted: "Not made up",
    statusClosed: "Closed",
    makeupSlot: (when: string) => `Makeup: ${when}`,
    startLabel: "Makeup start",
    assignButton: "Schedule makeup",
    working: "One moment…",
    cancelButton: "Cancel makeup",
    cancelTitle: "Cancel the makeup session?",
    cancelMessage: "The member will be notified and the absence goes back to the list awaiting a date.",
    cancelYes: "Cancel makeup",
    cancelNo: "Go back",
    decideCompleted: "It took place",
    decideNotCompleted: "It did not take place",
    problemInvalid: "Enter a valid start and end.",
    problemPast: "The date must be in the future.",
    problemOrder: "The end must be after the start.",
    problemDuration: "A makeup session can last from 15 minutes to 8 hours.",
    problemDeadline: "The date is after the last day for making up this session.",
    genericError: "Something went wrong. Please try again.",
    lockedGroup: "Group",
    lockedMakeup: "Makeup",
  },
  groups: {
    navGroups: "Groups",
    views: {
      mine: "My groups",
      schedule: "Schedule and payments",
      members: "Members",
      absences: "Absences and makeups",
      refunds: "Settlements",
    },
    calendar: {
      group: "Group",
      month: "Month",
      monthHint: "Click a day to select it, then click it again to add, move or remove its classes.",
      legendClasses: "Group classes",
      noMonthYet: "This month is not prepared yet.",
      locked: "This month is approved. Its dates can't be changed here; cancelling a class is in “Schedule and payments”.",
      pastBlocked: "You can't add a class in the past.",
      outsideMonth: "A class must stay within this month.",
      clash: (what: string) => `That time is taken: ${what}.`,
      added: "Class added.",
      exists: "A class at this time already exists on this day.",
      addedMany: (what, skipped) => `Added: ${what}.${skipped > 0 ? ` Skipped ${skipped} (past, taken time or already there).` : ""}`,
      addNone: (skipped) => `Nothing added: all ${skipped} were skipped (past, taken time or already there).`,
      moved: "Class moved.",
      deleted: "Class deleted.",
      pickDay: "Pick a day in the calendar.",
      dayTitle: (label) => label,
      addTitle: "Add a class",
      time: "Time",
      repeat: (weekday) => `Repeat every week of this month (${weekday})`,
      repeatHint: "The class is added on each such weekday through the end of the month.",
      add: "Add",
      dayClasses: "Classes of the group this day",
      dayEmpty: "No classes of the group this day.",
      moveTo: "Move to",
      move: "Move",
      remove: "Delete",
      close: "Close",
      kind: { session: "Training", block: "Blocked", draft: "Another group's class (draft)" },
      noGroups: "Create a group in “My groups” first.",
    },
    viewEmpty: "Create a group in “My groups” first.",
    goToMine: "Go to “My groups”",
    noPeriods: "No months prepared yet. Prepare a month in “My groups”.",
    noRefunds: "No refunds to members.",
    title: "Standing groups",
    subtitle: "Recurring classes with monthly billing for members.",
    emptyTitle: "You have no groups yet",
    emptyBody: "Create a group, share the link with members and bill classes month by month.",
    newGroup: "New group",
    createTitle: "Create a group",
    name: "Group name",
    namePlaceholder: "e.g. Tennis Tuesdays and Thursdays",
    sport: "Discipline",
    location: "Location",
    noLocation: "No location specified",
    capacity: "Number of seats",
    durationMinutes: "Duration (min)",
    paymentDueDays: "Payment deadline (days)",
    paymentDueDaysHint: "Days members have to pay after the month is approved.",
    rules: "Group rules",
    rulesPlaceholder: "E.g. cancellations, equipment, payment rules",
    visibility: "Visibility",
    visibilityLink: "Link only",
    visibilityPublic: "Public",
    visibilityLinkHint: "The group is visible only to people you send the link to.",
    visibilityPublicHint: "The group is visible to everyone on your profile.",
    createSportPlaceholder: "Choose a discipline",
    noSportHint: "To create a group, first add a training in the Offer tab — you pick the discipline from your trainings.",
    goToOffer: "Go to offer",
    cancelCreate: "Cancel",
    edit: "Edit",
    editTitle: "Edit group",
    options: "Group options",
    deleteGroup: "Delete group",
    deleteGroupBlocked: "A group with members can't be deleted.",
    deleteGroupTitle: "Delete group?",
    deleteGroupMessage: (name: string) => `The group "${name}" will be deleted with its whole schedule and settings. This can't be undone.`,
    deleteGroupTip: "Scheduled sessions stay on your calendar as regular sessions.",
    deleting: "Deleting…",
    editHint: "Changes apply to the coming months. Approved months, payments and the members' link stay as they are.",
    saveChanges: "Save changes",
    err: {
      nameRequired: "Enter a group name.",
      nameTooLong: (max) => `The name can have at most ${max} characters.`,
      sportRequired: "Pick a discipline from the list.",
      numberRequired: "Enter a number.",
      numberNotInteger: "Enter a whole number, without a decimal point or letters.",
      numberOutOfRange: (min, max) => `Enter a number from ${min} to ${max}.`,
      rulesTooLong: (max) => `The rules can have at most ${max} characters.`,
    },
    submit: "Create group",
    submitting: "Creating…",
    created: "The group has been created.",
    joinLink: "Link for members",
    copyLink: "Copy link",
    linkCopied: "Copied",
    copyFailed: "Could not copy. Please copy the link manually.",
    schedule: (days, time, minutes) => `${days}, ${time} (${minutes} min)`,
    scheduleByMonth: (minutes) => `Dates set month by month (${minutes} min)`,
    sessionsCount: (n) => `${n} ${n === 1 ? "session" : "sessions"}`,
    cancelSessionButton: "Cancel session",
    cancelShort: "Cancel",
    cancelSessionTitle: "Cancel this session?",
    cancelSessionMessage: (when, bookings, refund) =>
      bookings === 0
        ? `The session ${when} will be cancelled. Nobody has booked it. This cannot be undone.`
        : `The session ${when} will be cancelled for ${bookings} ${bookings === 1 ? "participant" : "participants"}.${
            refund
              ? ` We will refund them ${refund} in total.`
              : " Nobody has paid for this session yet, so nothing is refunded."
          } Participants will be notified. This cannot be undone.`,
    cancelSessionYes: "Cancel session",
    cancelSessionNo: "Back",
    cancelSessionDone: (refunds, failed) =>
      failed > 0
        ? `Session cancelled. Refunds: ${refunds}, failed: ${failed}. Try again in the Settlements tab.`
        : refunds > 0
          ? `Session cancelled. Refunds started: ${refunds}.`
          : "Session cancelled.",
    cancelSessionWorking: "Cancelling…",
    cancelSessionError: "Could not cancel the session. Try again.",
    refundsHeading: (n, unfinished) => (unfinished > 0 ? `Refunds (${n}, need attention: ${unfinished})` : `Refunds (${n})`),
    refundStatusPending: "In progress",
    refundStatusSucceeded: "Refunded",
    refundStatusFailed: "Failed",
    refundStatusExhausted: "Contact support",
    refundRowLine: (member, when) => `${member} · ${when}`,
    refundFailedHint:
      "The refund failed. The system will retry, or you can retry manually. Most common cause: not enough balance on your Stripe account.",
    refundExhaustedHint: "The refund failed after many attempts. Contact support.",
    refundRetry: "Retry refund",
    refundRetrying: "Retrying…",
    refundRetryDone: "Refund retry started.",
    refundRetryError: "Could not retry the refund. Try again.",
    sessionCancelledChip: "Cancelled",
    seatsLeft: (n, capacity) => `Seats left: ${n} of ${capacity}`,
    noApprovedPeriod: "No approved month yet",
    periodDraft: "Draft",
    periodApproved: "Approved",
    periodCancelled: "Cancelled",
    prepareMonthFor: (month) => `Prepare ${month}`,
    preparing: "Preparing…",
    fee: "Monthly fee (PLN)",
    feeHint: "Per member for the whole month, split over the sessions.",
    feeOutOfRange: (min, max) => `The amount must be between ${min} and ${max}.`,
    feeInvalid: "Enter a valid amount, e.g. 500 or 500.50.",
    termsTitle: (month) => `Dates · ${month}`,
    termsHint: "In a draft, click a session to turn it off; click an empty day to add a date.",
    noSession: "No session",
    dayOn: (label) => `Turn off: ${label}`,
    dayOff: (label) => `Add: ${label}`,
    feeTitle: "Fee per person",
    summaryMonthly: "Monthly fee",
    summaryPerSession: "Average per session",
    summaryStatus: "Status",
    approveNote: "An approved month can no longer be edited. Members will be charged the fee.",
    pastDatesBlock: (dates) => `Past dates cannot be approved: ${dates}.`,
    removePastDates: "Remove past dates",
    draftSummaryRounding: "The extra grosz are added to the first sessions.",
    save: "Save",
    saving: "Saving…",
    saved: "Saved",
    approve: "Approve month",
    approving: "Approving…",
    approveConfirm: (month, sessions, fee, members) =>
      `Approve ${month}? Sessions: ${sessions}, fee per member: ${fee}. Members to be charged: ${members}. Members will be charged and the sessions will appear in the calendar.`,
    approveConfirmYes: "Approve",
    approveConfirmNo: "Cancel",
    errorGeneric: "Something went wrong. Please try again.",
    members: "Members",
    noMembers: "Nobody has joined yet.",
    memberActive: "Active",
    memberPending: "Pending",
    memberEnded: "Ended",
    endMember: "End",
    endMemberConfirm: (name) => `End membership for ${name}?`,
    endMemberConfirmYes: "End",
    endMemberError: "Could not end the membership. Please try again.",
    confirmCancel: "Cancel",
    charges: "Member charges",
    noCharges: "No charges.",
    chargePending: "Payment due",
    chargePaid: "Paid",
    chargeExpired: "Overdue",
    chargeRefunded: "Refunded",
    dueDate: (date) => `Due: ${date}`,
    extendDays: (n) => `Extend by ${n} ${n === 1 ? "day" : "days"}`,
    extending: "Extending…",
    extendChargeError: "Could not extend the deadline. Please try again.",
    chargeAmountLabel: "Amount",
    extendExpiredNote: "Extending an expired charge needs a free seat in the group.",
    joinSubtitle: "Join this standing training group and pay for the month in one step.",
    joinTrainerBy: (trainer) => `Trainer: ${trainer}`,
    joinRulesHeading: "Group rules",
    joinDatesHeading: "Upcoming dates",
    joinPriceLine: (sessions, amount, month) =>
      `${sessions} ${sessions === 1 ? "session" : "sessions"} left in ${new Intl.DateTimeFormat("en-GB", { month: "long", timeZone: "UTC" }).format(new Date(`${month}-01T00:00:00Z`))} — ${amount}`,
    joinCta: "Join the group",
    joinChargeAmount: (amount) => `Amount for this month: ${amount}`,
    payLaterNote: (amount, days) =>
      days === 0
        ? `${amount} to pay — pay in My trainings.`
        : `${amount} to pay — you have ${days} ${days === 1 ? "day" : "days"} after joining, pay in My trainings.`,
    payCta: (amount) => `Pay ${amount}`,
    joining: "Processing…",
    joinNoOfferNote: "The trainer has not opened the next month yet. You can join the group now and you will get a payment link once the month is ready.",
    joinSignInIntro: "Create an account or sign in to join the group.",
    joinSignInCta: "Sign in to join",
    joinOwnGroup: "This is your group — participants join through this link. You manage it in the trainer panel.",
    joinManageGroup: "Go to group panel",
    joinVerifyEmail: "Confirm your email address to join the group and pay. Check your inbox — we sent you a link.",
    joinFull: "This group has no free seats left.",
    joinWaiting: "You are in the group. The trainer has not opened the next month yet — you will be notified with a payment link.",
    joinPaid: "This month is paid. You will find your sessions in My trainings.",
    joinGoToPanel: "My trainings",
    joinChargeExpired: "The payment deadline for this month has passed. Contact the trainer — they can extend it.",
    joinEnded: "Your membership in this group has ended.",
    joinRejoinCta: "Rejoin the group",
    joinChargeRefunded: "The payment for this month was refunded. You can pay for the next month once the trainer opens it.",
    joinDeadChargeNote: "Your previous charge has lapsed. Rejoin, then ask the trainer to extend your payment deadline.",
    serviceFeeLine: (amount) => `+ service fee: ${amount}`,
    dueSectionTitle: "To pay",
    dueRow: (group, month) => `${group} — ${month}`,
    dueUntil: (date) => `by ${date}`,
    dueSessions: (n) => `${n} ${n === 1 ? "session" : "sessions"}`,
    duePartial: (n, total) => `${n} of ${total} left`,
    payCtaFor: (amount, label) => `Pay ${amount} for ${label}`,
    dueExpiredNote: "Deadline passed — contact the trainer.",
    paymentProcessing: "Thank you! We are processing your payment — your sessions will appear here shortly.",
    paymentRefresh: "Refresh the page",
    paymentCancelled: "The payment was cancelled. You can pay for the month any time before the deadline.",
    paying: "Redirecting to payment…",
  },
  clientGroups: {
    navGroups: "My groups",
    empty: "You are not in any group yet. Join with a link from your trainer.",
    memberInvited: "Invited",
    noCharge: "No charge",
    noMonths: "The trainer has not approved any month yet.",
    trainerLabel: "Trainer",
    participant: (name) => `Participant: ${name}`,
    absencesTitle: "Absences",
    notIncluded: "Not in your charge",
    joinedTitle: "You joined the group",
    joinedMessage: (group, trainer) => `You were added to the group "${group}" by ${trainer}.`,
    joinedPayNow: (amount, due) => `To pay: ${amount}, ${due}. Pay below, in the group tab.`,
    joinedPaid: "The current month is already paid.",
    joinedNoCharge: "Payment will appear once the trainer approves the next month.",
    joinedOk: "View group",
  },
  holidays: {
    notSignedIn: "You're not signed in.",
    needsTrainerProfile: "Create your trainer profile first.",
    invalidDate: "Invalid date.",
    notHoliday: "This day is not a public holiday.",
    pastDate: "This day has already passed.",
    tooFarAhead: "This day is too far ahead.",
    legend: "Holiday",
    dayOff: (name) => `${name} — public holiday`,
    dayOffOpened: (name) => `${name} — public holiday, open for bookings`,
    weekOpenNote: (name) => `${name} · open for bookings`,
    openDay: "Open this day for bookings",
    closeDay: "Close this day (holiday)",
    toggleFailed: "Couldn't change this day's setting. Please try again.",
    bookingDayOff: (name) => `Public holiday: ${name}. The trainer is not taking bookings.`,
  },
  mockPay: {
    metaTitle: "Test payment",
    banner: "Test payment — not a real one",
    bannerNote: "Development environment: no money will be taken.",
    heading: "Payment summary",
    total: "Total",
    platformFee: (amount) => `Platform commission included: ${amount}`,
    payButton: "Pay",
    backButton: "Back",
    paidState: "This payment has already been made.",
    expiredState: "This payment has expired.",
    backLink: "Back to the site",
  },
};
